import type { KeyboardEvent } from "react";
import { useUi, useUiDispatch } from "@/app/AppContext";
import { pickWorstStatus, type SystemDomain } from "@/domain/systemAnalysis";
import { NOT_RELEVANT, scoreFor, type TrendValue } from "@/domain/systemAnalysisTrend";
import { useI18n } from "@/i18n/useI18n";
import { CHECKPOINT_SHORT_NAMES } from "./checkpointShortNames";
import { CheckpointTrendRow, TrendYearsHeader } from "./CheckpointTrendRow";
import { pick, statusLabelKey } from "./useSystemAnalysis";

const LONG_DESCRIPTION = 120;

/** "▲ +38 pp", "▼ −5 pp" or "■ 0 pp", with the matching class. */
function ScoreChange({ from, to }: { from: number | null; to: number | null }) {
  if (from === null || to === null) return null;
  const change = to - from;
  const [symbol, direction] = change > 0 ? ["▲", "up"] : change < 0 ? ["▼", "down"] : ["■", "same"];
  return (
    <strong className={`system-trend-change system-trend-change-${direction}`}>
      {`${symbol} ${change > 0 ? "+" : change < 0 ? "−" : ""}${Math.abs(change)} pp`}
    </strong>
  );
}

const percent = (score: number | null) => (score === null ? "–" : `${score}%`);

interface ElementTrendCardProps {
  domain: SystemDomain;
  years: number[];
  statusByYear: Record<number, Record<string, TrendValue>>;
  mode: "history" | "firstYear";
}

export function ElementTrendCard({ domain, years, statusByYear, mode }: ElementTrendCardProps) {
  const { t, language } = useI18n();
  const { system } = useUi();
  const dispatch = useUiDispatch();
  const valuesFor = (year: number) =>
    domain.items.map((item) => statusByYear[year]?.[item.id] ?? NOT_RELEVANT);
  const firstYear = years[0]!;
  const lastYear = years.at(-1)!;
  const lastValues = valuesFor(lastYear);
  // Same rule and colours as the single analysis: the worst status, last year.
  const band = pickWorstStatus(lastValues.map((value) => value.source));
  const startScore = scoreFor(valuesFor(firstYear));
  const endScore = scoreFor(lastValues);

  const description = pick(language, domain.descriptionNo, domain.descriptionEn);
  const descriptionExpanded = system.expandedDescriptions.has(domain.id);
  const descriptionToggles = description.length > LONG_DESCRIPTION;
  const toggleDescription = () =>
    dispatch({ type: "toggleSystemDescription", domainId: domain.id });
  const onDescriptionKey = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleDescription();
    }
  };

  return (
    <section className="system-domain-card system-trend-card">
      <div className={`system-domain-header system-domain-header-${band}`}>
        <span
          className={`system-domain-badge system-status-${band}`}
          title={t(statusLabelKey(band))}
        >
          {domain.id}
        </span>
        <div className="system-trend-header-text">
          <div className="system-trend-title-row">
            <h4 className="system-domain-title">
              {pick(language, domain.titleNo, domain.titleEn)}
            </h4>
            <span className="system-trend-score">
              {mode === "firstYear" ? (
                <>
                  {`${lastYear}: `}
                  <strong>{percent(endScore)}</strong>
                  {` · ${lastYear + 1}: –`}
                </>
              ) : (
                <>
                  {`${percent(startScore)} `}
                  <span className="system-trend-arrow">→</span>
                  {` ${percent(endScore)} `}
                  <ScoreChange from={startScore} to={endScore} />
                </>
              )}
            </span>
          </div>
          {description &&
            (descriptionToggles ? (
              <div
                className={`system-domain-description system-domain-description-clickable${descriptionExpanded ? "" : " system-domain-description-collapsed"}`}
                role="button"
                tabIndex={0}
                aria-expanded={descriptionExpanded}
                onClick={toggleDescription}
                onKeyDown={onDescriptionKey}
              >
                {description}
              </div>
            ) : (
              <div className="system-domain-description system-domain-description-collapsed">
                {description}
              </div>
            ))}
        </div>
      </div>
      <div className="system-trend-body">
        <div className="system-trend-row system-trend-row-header">
          <span className="system-trend-checkpoint-heading">{t("systemCheckpoint")}</span>
          <TrendYearsHeader years={years} mode={mode} />
        </div>
        {!domain.items.length && <div className="system-empty-domain">{t("systemNoCriteria")}</div>}
        {domain.items.map((item) => {
          const values = years.map((year) => statusByYear[year]?.[item.id] ?? NOT_RELEVANT);
          const title = pick(language, item.titleNo, item.titleEn);
          const shortName = CHECKPOINT_SHORT_NAMES[item.id];
          const statusText = years
            .map((year, index) => `${year} ${t(statusLabelKey(values[index]!.source))}`)
            .join(", ");
          return (
            <div
              key={item.id}
              className="system-trend-row"
              title={`${item.id} ${title}\n${statusText}`}
            >
              <span className="system-trend-label">
                {`${item.id.replace(/\./g, "")}. ${(shortName && pick(language, shortName.no, shortName.en)) || title}`}
              </span>
              <CheckpointTrendRow
                id={item.id}
                values={values}
                mode={mode}
                ariaLabel={`${item.id}: ${statusText}`}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}
